import { Link2, type LucideIcon, Upload } from "lucide-react";

import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import { Dialog, DialogContent } from "../ui/dialog";
import type { Tier } from "../ui/tier-badge";

export interface TierExplainerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentTier: Tier;
  suggestedUpgrade: Tier;
  onPrimaryCta: () => void;
}

type TierRowCopy = {
  rank: string;
  name: string;
  summary: string;
};

type PassportTierName = Exclude<Tier, "lender-approved">;

const TIER_ORDER: PassportTierName[] = [
  "self-reported",
  "document-backed",
  "bank-verified",
];

const TIER_ROW_COPY: Record<PassportTierName, TierRowCopy> = {
  "self-reported": {
    rank: "Tier 1",
    name: "Self-reported",
    summary: "Basic info provided. Not verified.",
  },
  "document-backed": {
    rank: "Tier 2",
    name: "Document-backed",
    summary: "At least two of income, cash/assets, and debts have document-level or stronger evidence.",
  },
  "bank-verified": {
    rank: "Tier 3",
    name: "Securely verified",
    summary: "At least two of income, cash/assets, and debts have connected-source evidence.",
  },
};

type CtaLabels = { inline: string; inlineIcon: LucideIcon; footer: string };

// SILL-539: bank connection is free, so the bank-verified rung has one
// label and is never disabled. The previous signature took a
// hasPlaidEntitlement flag and rendered "Connect my bank (Plus)".
function ctaLabels(suggested: Tier): CtaLabels | null {
  switch (suggested) {
    case "document-backed":
      return {
        inline: "Upload documents",
        inlineIcon: Upload,
        footer: "Upgrade to Tier 2",
      };
    case "bank-verified":
      return {
        inline: "Connect my bank",
        inlineIcon: Link2,
        footer: "Upgrade to Tier 3",
      };
    case "self-reported":
    case "lender-approved":
      return null;
  }
}

type StepState = "past" | "current" | "future";

function stepState(tier: PassportTierName, currentTier: PassportTierName): StepState {
  const idx = TIER_ORDER.indexOf(tier);
  const currentIdx = TIER_ORDER.indexOf(currentTier);
  if (idx < currentIdx) return "past";
  if (idx === currentIdx) return "current";
  return "future";
}

export function TierExplainerModal({
  open,
  onOpenChange,
  currentTier,
  suggestedUpgrade,
  onPrimaryCta,
}: TierExplainerModalProps) {
  const displayedCurrentTier: PassportTierName = currentTier === "lender-approved" ? "bank-verified" : currentTier;
  const labels =
    suggestedUpgrade === displayedCurrentTier ? null : ctaLabels(suggestedUpgrade);
  const isPrimaryCtaDisabled = false;
  const currentRank = TIER_ROW_COPY[displayedCurrentTier].rank;
  const currentName = TIER_ROW_COPY[displayedCurrentTier].name;

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent
        className="sm:max-w-lg"
        description="Tiers reflect how verified your finances are. More verification = better pricing + priority."
        title="Tier system"
      >
        <p className="-mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          <span>You're currently at</span>
          <span className="font-semibold text-foreground">
            {currentRank} — {currentName}
          </span>
        </p>

        <ol
          className="relative mt-4 space-y-1"
          data-slot="tier-stepper"
          role="list"
        >
          <span
            aria-hidden="true"
            className="pointer-events-none absolute left-[6.5px] top-3 bottom-3 w-px bg-border"
          />
          {TIER_ORDER.map((tier) => {
            const isSuggested = suggestedUpgrade === tier;
            return (
              <TierRow
                inlineCtaLabel={isSuggested ? (labels?.inline ?? null) : null}
                inlineCtaIcon={
                  isSuggested ? (labels?.inlineIcon ?? null) : null
                }
                inlineCtaDisabled={isSuggested && isPrimaryCtaDisabled}
                currentTier={displayedCurrentTier}
                key={tier}
                onInlineCtaClick={onPrimaryCta}
                tier={tier}
              />
            );
          })}
        </ol>

        {labels ? (
          <div className="mt-5 border-t border-border pt-4">
            <Button
              className="w-full sm:w-auto"
              data-slot="tier-explainer-primary-cta"
              disabled={isPrimaryCtaDisabled}
              onClick={onPrimaryCta}
            >
              {labels.footer}
            </Button>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function TierRow({
  tier,
  currentTier,
  inlineCtaLabel,
  inlineCtaIcon: InlineIcon,
  inlineCtaDisabled,
  onInlineCtaClick,
}: {
  tier: PassportTierName;
  currentTier: PassportTierName;
  inlineCtaLabel: string | null;
  inlineCtaIcon: LucideIcon | null;
  inlineCtaDisabled: boolean;
  onInlineCtaClick: () => void;
}) {
  const rowCopy = TIER_ROW_COPY[tier];
  const state = stepState(tier, currentTier);
  const isCurrent = state === "current";
  const isFuture = state === "future";
  const disabled = false;

  return (
    <li
      aria-current={isCurrent ? "step" : undefined}
      aria-disabled={disabled ? "true" : undefined}
      className={cn(
        "relative",
        isFuture && !disabled && "opacity-75",
        disabled && "opacity-55",
      )}
      data-state={isCurrent ? "current" : "default"}
      data-step-state={state}
      data-tier-row={tier}
    >
      <span
        aria-hidden="true"
        className={cn(
          "absolute left-0 top-3 h-3.5 w-3.5 rounded-full transition-colors",
          state === "past" && "bg-primary/40",
          state === "current" && "bg-primary ring-2 ring-primary/25",
          state === "future" && "bg-background ring-1 ring-border",
        )}
        data-step-dot={state}
      />
      <div
        className={cn(
          "ml-5 rounded-lg py-2 pl-1 pr-3 transition-colors",
          isCurrent && "bg-primary/5 ring-1 ring-inset ring-primary/20",
        )}
      >
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="text-base font-semibold tracking-[-0.01em]">
            {rowCopy.rank}
          </span>
          <span className="text-xs uppercase tracking-[0.08em] text-muted-foreground">
            {rowCopy.name}
          </span>
        </div>
        <p className="mt-0.5 text-sm text-muted-foreground">{rowCopy.summary}</p>
        {tier === "bank-verified" ? (
          <p className="mt-0.5 text-sm font-medium text-primary-dark">
            Unlock best pricing + priority access
          </p>
        ) : null}
        {inlineCtaLabel ? (
          <button
            className={cn(
              "mt-1.5 inline-flex items-center gap-1.5 rounded text-sm font-medium text-primary-dark transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
              inlineCtaDisabled
                ? "cursor-not-allowed opacity-50"
                : "hover:text-primary",
            )}
            data-slot="tier-explainer-inline-cta"
            disabled={inlineCtaDisabled}
            onClick={onInlineCtaClick}
            type="button"
          >
            {InlineIcon ? (
              <InlineIcon aria-hidden="true" className="h-4 w-4" />
            ) : null}
            <span
              className={cn(
                "underline-offset-2",
                !inlineCtaDisabled && "hover:underline",
              )}
            >
              {inlineCtaLabel}
            </span>
          </button>
        ) : null}
      </div>
    </li>
  );
}
